import { MessageChunk } from '@/models/thread';

/*
 * Answers cite their sources inline. While streaming, markers arrive bare,
 * e.g. `[3]`. In the finished answer the backend links each one to its
 * source, e.g. `[[3]](https://…)`. The number is the 1-based position in
 * `rag_sources` followed by `quantitative_sources`.
 */
const LINKED_MARKER = /\[\[(\d+)\]\]\(/g;
const LINKED_MARKER_WITH_URL = /\[\[(\d+)\]\]\([^)\s]*\)/g;
const PARTIAL_MARKER_AT_END = /\[\d*$/;

/* Why streamed markers can't be opened yet; shown on hover and read once to screen readers */
export const PENDING_CITATION_MESSAGE =
  'Citations will be available once the answer finishes';

export const isValidSourceNumber = (
  source: number,
  sourcesCount: number
): boolean => Number.isInteger(source) && source >= 1 && source <= sourcesCount;

/* Legacy answers keep their citations in `chunk.citation`, not in the text */
const hasLegacyCitations = (chunk: MessageChunk): boolean =>
  Array.isArray(chunk.citation) && chunk.citation.length > 0;

/* How many valid inline markers point to each source number */
export const getInlineCitations = (
  response: MessageChunk[],
  sourcesCount: number
): Map<number, number> => {
  const counts = new Map<number, number>();

  for (const chunk of response) {
    if (hasLegacyCitations(chunk)) continue;

    for (const match of chunk.text.matchAll(LINKED_MARKER)) {
      const source = Number(match[1]);
      if (isValidSourceNumber(source, sourcesCount)) {
        counts.set(source, (counts.get(source) ?? 0) + 1);
      }
    }
  }

  return counts;
};

export const hasValidInlineCitations = (
  response: MessageChunk[],
  sourcesCount: number
): boolean => getInlineCitations(response, sourcesCount).size > 0;

/* Hide a marker that is still being typed, e.g. `[` or `[1` */
export const stripPartialMarker = (text: string): string =>
  text.replace(PARTIAL_MARKER_AT_END, '');

/* `[[3]](https://…)` → `[3]`, matching the numbered source list on copy */
export const toPlainCitations = (text: string): string =>
  text.replace(LINKED_MARKER_WITH_URL, '[$1]');
